import { Auth } from '@aws-amplify/auth';
import { getSessionStorageRecord, setSessionStorageRecord } from 'application';
import axios from 'axios';
import { io, Socket } from 'socket.io-client';
import {
  WOUNDSCRIBE_SOCKET_EVENTS,
  WOUNDSCRIBE_SOCKET_NAMESPACE,
} from 'woundscribe/core/constants';
import type { RecordingGeolocation } from 'woundscribe/types';

export type SocketSession = {
  socket: Socket;
  sessionId: string | null;
};

export type TranscriptPayload = {
  sessionId: string;
  text: string;
  speaker?: string;
  startMs: number;
  endMs: number;
  /** False for the in-progress caption; true once Soniox confirms the text. */
  isFinal?: boolean;
};

type CreateSocketSessionOpts = {
  onDisconnect?: (reason: string) => void;
  onConnectError?: (err: Error) => void;
  onTranscription?: (payload: TranscriptPayload) => void;
};

const START_SESSION_TIMEOUT_MS = 10000;
const STOP_SESSION_TIMEOUT_MS = 15000;
const AUTHORIZATION_TOKENS_REQUIRED = 'Authorization Tokens Required';

function hasCredential(value?: string): boolean {
  return !!value?.replace(/^Bearer\s+/i, '').trim();
}

function socketAuthCookieSuffix(): string {
  const secure = globalThis.location?.protocol === 'https:' ? '; Secure' : '';
  return `; Path=/socket.io; SameSite=Lax; Max-Age=60${secure}`;
}

function setSocketAuthCookies(authorization: string, idToken: string): void {
  document.cookie = `authorization=${encodeURIComponent(authorization)}${socketAuthCookieSuffix()}`;
  document.cookie = `woundtech-id-token=${encodeURIComponent(idToken)}${socketAuthCookieSuffix()}`;
}

function clearSocketAuthCookies(): void {
  document.cookie = 'authorization=; Path=/socket.io; Max-Age=0';
  document.cookie = 'woundtech-id-token=; Path=/socket.io; Max-Age=0';
}

async function getGatewayAuthHeaders(): Promise<{
  authorization: string;
  'woundtech-id-token': string;
}> {
  const isInternal = getSessionStorageRecord('is-internal');
  let token = getSessionStorageRecord('access-token');
  let idToken = getSessionStorageRecord('id-token');

  if (isInternal === 'true') {
    const refreshToken = getSessionStorageRecord('refresh-token');
    const expiry = Number(getSessionStorageRecord('expiry')) || 0;
    if (refreshToken && expiry) {
      const currentTime = Math.floor(Date.now() / 1000);
      const REMAINING_SECONDS_TO_EXPIRE = 1000;
      if (expiry - currentTime < REMAINING_SECONDS_TO_EXPIRE) {
        const response = await axios.get(
          `${(globalThis as any)._env_?.REFRESH_TOKEN_SSO_URI || '/refreshToken'}`,
          {
            headers: {
              refreshToken: getSessionStorageRecord('refresh-token'),
            },
          }
        );
        token = response.data['access_token'];
        idToken = response.data['id_token'];
        setSessionStorageRecord('access-token', token);
        setSessionStorageRecord('id-token', idToken);
        setSessionStorageRecord('expiry', response.data['expiry']);
      }
    }
  } else {
    const session = await Auth.currentSession();
    token = session.getAccessToken().getJwtToken();
    idToken = session.getIdToken().getJwtToken();
    setSessionStorageRecord('access-token', token);
    setSessionStorageRecord('id-token', idToken);
  }

  return {
    authorization: token ? `Bearer ${token}` : '',
    'woundtech-id-token': idToken || '',
  };
}

/**
 * Connects to the gateway's ambient-session namespace.
 * Resolves when connected, rejects on failure.
 * reconnection is intentionally off — the store re-creates the session on
 * recording start so we don't silently resume a stale connection.
 *
 * Audio is exchanged on a WebSocket. The browser sends the existing Cognito
 * session as a short-lived cookie on that handshake. The gateway rejects the
 * socket unless those credentials are valid, then discards the cookie.
 */
export async function createSocketSession(
  opts: CreateSocketSessionOpts
): Promise<SocketSession> {
  const authHeaders = await getGatewayAuthHeaders();
  if (
    !hasCredential(authHeaders.authorization) ||
    !hasCredential(authHeaders['woundtech-id-token'])
  ) {
    throw new Error(AUTHORIZATION_TOKENS_REQUIRED);
  }

  return new Promise((resolve, reject) => {
    setSocketAuthCookies(
      authHeaders.authorization,
      authHeaders['woundtech-id-token']
    );

    // Local: WOUNDSCRIBE_REALTIME_URI=http://localhost:8888
    // Deployed: leave unset (same-origin → CloudFront → gateway)
    const baseUrl = (globalThis as any)._env_?.WOUNDSCRIBE_REALTIME_URI || '';
    const socket = io(`${baseUrl}${WOUNDSCRIBE_SOCKET_NAMESPACE}`, {
      transports: ['websocket'],
      reconnection: false,
    });

    socket.on(
      WOUNDSCRIBE_SOCKET_EVENTS.TRANSCRIPTION,
      (payload: TranscriptPayload) => {
        opts.onTranscription?.(payload);
      }
    );

    socket.once('connect', () => {
      clearSocketAuthCookies();
      resolve({ socket, sessionId: null });
    });

    socket.once('connect_error', (err) => {
      clearSocketAuthCookies();
      opts.onConnectError?.(err);
      socket.disconnect();
      reject(err);
    });

    socket.on('disconnect', (reason) => {
      opts.onDisconnect?.(reason);
    });
  });
}

/**
 * Signals WoundScribe to begin a new audio session and waits for the
 * server ack so `session.sessionId` is set before any audio-chunk is sent.
 */
export function emitStartSession(
  session: SocketSession,
  payload: {
    encounterId: string;
    patientId?: string;
    mimeType: string;
    /** Browser geolocation outcome. Coordinates are null unless CAPTURED. */
    geolocationStatus: RecordingGeolocation['status'];
    latitude: number | null;
    longitude: number | null;
  }
): Promise<string> {
  return new Promise((resolve, reject) => {
    session.socket
      .timeout(START_SESSION_TIMEOUT_MS)
      .emit(
        WOUNDSCRIBE_SOCKET_EVENTS.START_SESSION,
        payload,
        (err: Error | null, ack?: { sessionId?: string }) => {
          if (err) {
            reject(err);
            return;
          }
          const sessionId = ack?.sessionId;
          if (!sessionId) {
            reject(new Error('start-session ack missing sessionId'));
            return;
          }
          session.sessionId = sessionId;
          resolve(sessionId);
        }
      );
  });
}

/**
 * Sends a raw audio chunk as binary.
 * No-ops until the socket is connected and start-session has assigned a sessionId.
 */
export function emitAudioChunk(
  session: SocketSession | null | undefined,
  buffer: ArrayBuffer
) {
  if (!session?.socket?.connected || !session.sessionId) {
    return;
  }
  session.socket.emit(WOUNDSCRIBE_SOCKET_EVENTS.AUDIO_CHUNK, buffer);
}

/**
 * Pauses the backend ASR stream (Soniox keepalive) while the mic is paused
 * — e.g. tab hidden / screen locked — so the vendor does not idle-timeout.
 */
export function emitPauseSession(session: SocketSession) {
  if (!session.socket.connected || !session.sessionId) {
    return;
  }
  session.socket.emit(WOUNDSCRIBE_SOCKET_EVENTS.PAUSE_SESSION, {
    sessionId: session.sessionId,
  });
}

/**
 * Resumes the backend ASR stream after a pause.
 */
export function emitResumeSession(session: SocketSession) {
  if (!session.socket.connected || !session.sessionId) {
    return;
  }
  session.socket.emit(WOUNDSCRIBE_SOCKET_EVENTS.RESUME_SESSION, {
    sessionId: session.sessionId,
  });
}

/**
 * Signals WoundScribe the stream is finished and waits for the server ack
 * so late `transcription` events can arrive before we disconnect.
 */
export function emitStopSession(
  session: SocketSession
): Promise<{ sessionId: string; status: string } | void> {
  if (!session.socket.connected || !session.sessionId) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    session.socket
      .timeout(STOP_SESSION_TIMEOUT_MS)
      .emit(
        WOUNDSCRIBE_SOCKET_EVENTS.STOP_SESSION,
        { sessionId: session.sessionId },
        (err: Error | null, ack?: { sessionId?: string; status?: string }) => {
          if (err) {
            reject(err);
            return;
          }
          resolve({
            sessionId: ack?.sessionId ?? session.sessionId!,
            status: ack?.status ?? 'unknown',
          });
        }
      );
  });
}

export function disconnectSocketSession(session: SocketSession) {
  session.socket.disconnect();
}
